import React, { ComponentType } from "react"
import { NodeProps } from "reactflow"
import { Grid, Tooltip } from "@material-ui/core"

import { BlockNodeData } from "@/protocols/chatBotFlow"

import useChatBotFlowBlockDetails from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/hooks/useChatBotFlowBlockDetails"

import EditTimeoutConditionSection from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/EditTimeoutConditionSection"
import BaseBlock from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/BaseBlock"
import BlockBodySection from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/BlockBodySection"
import BlockBodySectionContainer from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/BlockBodySectionContainer"
import BlockBodySectionGroup from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/BlockBodySectionGroup"
import ContrastChip from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/ContrastChip"
import APIUrlSectionEditor from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/resources/Blocks/AIAgentAPIBlock/APIUrlSectionEditor"
import APIOutputConditionSectionEditor from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/resources/Blocks/AIAgentAPIBlock/APIOutputConditionSectionEditor"
import APIOutputConditionSectionContainer from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/resources/Blocks/AIAgentAPIBlock/APIOutputConditionSectionContainer"
import BlockBodySectionHandle from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/BlockBodySectionHandle"
import CreateConditionButton from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/CreateConditionButton"
import EditMaxBlockCallingConditionSection from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/resources/Blocks/AIAgentAPIBlock/EditMaxBlockCallingConditionSection"

import { getConditionRuleByValidationSlug, getContinueConditionRuleFormattedForCreation } from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/utils/chatBotFlowBlockRule"

import useCustomStyles from "@/styles/custom"

import { generateUUID } from "@/utils/id"
import AIAgentAPIBlockCustomFieldSectionEditor from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/resources/Blocks/AIAgentAPIBlock/AIAgentAPIBlockCustomFieldSectionEditor"
import { getCurrentCustomFieldWebhookAssociationDataInAIAgentAPIBlock } from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/utils/chatBotFlowBlockContent"
import { colors } from "@/styles/colors"

const AIAgentAPIBlock: ComponentType<NodeProps<BlockNodeData>> = (node) => {
	const {
		changeContent,
		content
	} = useChatBotFlowBlockDetails({ chatBotFlowBlockId: node.data.chatBotFlowBlockId })

	const chatBotFlowBlockDetails = useChatBotFlowBlockDetails({ chatBotFlowBlockId: node.data.chatBotFlowBlockId })

	const customClasses = useCustomStyles()

	const apiUrl = content?.aiAgentAPI?.apiUrl

	const aiAgentAPIOutputConditionRules = chatBotFlowBlockDetails.nextChatBotFlowBlockRules.filter((nextChatBotFlowBlockRule) => getConditionRuleByValidationSlug("ai-agent-api-output", [nextChatBotFlowBlockRule]))

	const getCustomFieldData = () => {
		const customFieldAssociations = getCurrentCustomFieldWebhookAssociationDataInAIAgentAPIBlock(chatBotFlowBlockDetails.content)

		if (customFieldAssociations?.length > 0) {
			return (
				<>
					{customFieldAssociations.map((customField, index) => {
						return (<Grid container direction="row" wrap="nowrap" key={index} spacing={1}>
							<Grid item>
								<ContrastChip
									backgroundColor={colors.gray[100]}
									style={{
										maxWidth: "16ch",
										overflow: "hidden",
										textOverflow: "ellipsis",
										whiteSpace: "nowrap"
									}}
									title={customField?.customFieldName || "Campo não mapeado"}
								/>
							</Grid>
							<Grid item xs zeroMinWidth>
								<ContrastChip
									backgroundColor={colors.blue[100]}
									style={{
										maxWidth: "15ch",
										overflow: "hidden",
										textOverflow: "ellipsis",
										whiteSpace: "nowrap"
									}}
									title={customField?.webhookField || "Campo não mapeado"}
								/>
							</Grid>
						</Grid>)
					})}
				</>
			)
		} else {
			return <Grid item>
				<ContrastChip
					backgroundColor={colors.gray[100]}
					style={{
						overflow: "hidden",
						textOverflow: "ellipsis",
						whiteSpace: "nowrap"
					}}
					title={"Nenhum campo personalizado mapeado"}
				/>
			</Grid>
		}
	}

	return (
		<BaseBlock
			node={node}
		>
			<BlockBodySection
				fullWidth
			>
				<APIUrlSectionEditor
					onSave={changeContent}
					chatBotFlowBlockContent={content}
				>
					<Grid
						container
						direction="column"
					>
						<BlockBodySectionContainer
							text={apiUrl ? (
								<Tooltip
									title={apiUrl}
									placement="right-end"
								>
									<Grid
										style={{
											width: "100%"
										}}
									>
										<ContrastChip
											title={apiUrl}
											backgroundColor={colors.blue[400]}
											className={customClasses.inlineText}
											style={{
												display: "inline-flex"
											}}
										/>
									</Grid>
								</Tooltip>
							) : ""}
							title="URL da API do Agente:"
							placeholder="Nenhuma URL configurada"
						/>
					</Grid>
				</APIUrlSectionEditor>
			</BlockBodySection>

			<BlockBodySectionGroup>
				{aiAgentAPIOutputConditionRules.map(rule => (
					<BlockBodySection
						key={rule.id}
					>
						<BlockBodySectionHandle
							chatBotFlowBlockRule={rule}
						>
							<APIOutputConditionSectionEditor
								onSave={(chatBotFlowBlockRule) => chatBotFlowBlockDetails.changeRule("UPDATE", chatBotFlowBlockRule)}
								onDelete={() => chatBotFlowBlockDetails.changeRule("DELETE", rule)}
								chatBotFlowBlockRule={rule}
							>
								<APIOutputConditionSectionContainer
									chatBotFlowBlockRule={rule}
									chatBotFlowBlockId={node?.data?.chatBotFlowBlockId}
								/>
							</APIOutputConditionSectionEditor>
						</BlockBodySectionHandle>
					</BlockBodySection>
				))}
			</BlockBodySectionGroup>

			<BlockBodySection>
				<APIOutputConditionSectionEditor
					hideChildSelection
					onSave={(chatBotFlowBlockRule) => (
						chatBotFlowBlockDetails.changeRule("CREATE", {
							...chatBotFlowBlockRule,
							id: generateUUID(),
							next_chat_bot_flow_block_id: null,
							validations: chatBotFlowBlockRule?.validations || []
						})
					)}
					chatBotFlowBlockRule={(
						getContinueConditionRuleFormattedForCreation(
							chatBotFlowBlockDetails.blockConstructionResource?.available_next_block_flow_rules,
							"ai-agent-api-output"
						)
					)}
				>
					<CreateConditionButton
						color={chatBotFlowBlockDetails.blockThemeConfig.color.blockIcon}
						chatBotFlowBlockId={node?.data?.chatBotFlowBlockId}
						errorType="condition_block_missing_option"
					>
						Adicionar condição
					</CreateConditionButton>
				</APIOutputConditionSectionEditor>
			</BlockBodySection>

			<BlockBodySectionGroup>
				<BlockBodySection
					fullWidth
				>
					<AIAgentAPIBlockCustomFieldSectionEditor
						onSave={content => chatBotFlowBlockDetails.changeContent(content)}
						chatBotFlowBlockContent={chatBotFlowBlockDetails.content}
					>
						<BlockBodySectionContainer
							text={getCustomFieldData()}
							placeholder="Nenhum campo mapeado"
							title="Salvar Resposta"
							chatBotFlowBlockId={node?.data?.chatBotFlowBlockId}
						/>
					</AIAgentAPIBlockCustomFieldSectionEditor>
				</BlockBodySection>
			</BlockBodySectionGroup>

			<EditMaxBlockCallingConditionSection
				nextChatBotFlowBlockRules={chatBotFlowBlockDetails.nextChatBotFlowBlockRules}
				onSave={(chatBotFlowBlockRule) => chatBotFlowBlockDetails.changeRule("UPDATE", chatBotFlowBlockRule)}
				renderText={(value) => <span>após {value}</span>}
			/>

			<EditTimeoutConditionSection
				nextChatBotFlowBlockRules={chatBotFlowBlockDetails.nextChatBotFlowBlockRules}
				onSave={(chatBotFlowBlockRule) => chatBotFlowBlockDetails.changeRule("UPDATE", chatBotFlowBlockRule)}
				renderText={(timeText) => <span>{timeText} sem interação</span>}
				chatBotFlowChannelType={node.data.chatBotFlowChannelType}
			/>
		</BaseBlock>
	)
}

export default AIAgentAPIBlock
